import discord from "../../assets/discord.svg";
import instagram from "../../assets/instagram.svg";
import linkedin from "../../assets/linkedin.svg";
import reddit from "../../assets/reddit.svg";
import telegram from "../../assets/telegram.svg";
import tiktok from "../../assets/tiktok.svg";
import twitter from "../../assets/twitter.svg";
import { checkIfUserHasPermissionForAction } from "../../solana/common/helpers/common.helpers";
import { ClubType, TreasuryFundraiseStatus } from "../enums/club.enum";
import {
  CreateClubWizardSteps,
  GovernanceConfigurationType,
} from "../enums/clubs-wizard.enum";
import { ClubAction } from "../enums/common.enums";
import { IConfigurationItem } from "../interfaces/clubs-wizard.interface";
import { IClubTreasury, RoleConfig } from "../interfaces/clubs.interface";
import { ProposalTypeNew } from "../interfaces/proposals.interface";

export const CREATE_CLUB_TOTAL_STEPS = 8;
export const CLUB_TAGS_MAX_LENGTH = 20;
export const MAX_TAG_LENGTH = 21;
export const CLUB_NAME_MAX_LENGTH = 32;
export const MIN_VOTING_DAYS = 7;
export const MAX_VOTING_DAYS = 255;

export const PROJECT_FUNDRAISE_WITHDRAWAL_QUORUM = 95;
export const PROJECT_FUNDRAISE_TRANSFER_QUORUM = 51;
export const PROJECT_FUNDRAISE_MAX_VOTING_TIME = 30;

export const SOCIAL_NETWORKS = [
  "discord",
  "twitter",
  "instagram",
  "reddit",
  "linkedin",
  "tiktok",
  "telegram",
];

export const SOCIAL_NETWORK_ICONS = {
  twitter,
  discord,
  reddit,
  linkedin,
  instagram,
  telegram,
  tiktok,
};

//TODO@milica-monke-merge:
//Monke is Role based by default and some steps from Role based are removed for Monke
export const clubTypeSteps: Map<
  GovernanceConfigurationType,
  CreateClubWizardSteps[]
> = new Map([
  [
    GovernanceConfigurationType.ExistingToken,
    [
      CreateClubWizardSteps.BasicInfo,
      CreateClubWizardSteps.GovernanceConfiguration,
      CreateClubWizardSteps.Roles,
      CreateClubWizardSteps.Members,
      CreateClubWizardSteps.Verification,
      CreateClubWizardSteps.FundraisingConfiguration,
      CreateClubWizardSteps.FinancialConfiguration,
      CreateClubWizardSteps.Preview,
    ],
  ],
  [
    GovernanceConfigurationType.RoleBased,
    [
      CreateClubWizardSteps.BasicInfo,
      CreateClubWizardSteps.Members,
      CreateClubWizardSteps.Preview,
    ],
  ],
  [
    GovernanceConfigurationType.NFT,
    [
      CreateClubWizardSteps.BasicInfo,
      CreateClubWizardSteps.GovernanceConfiguration,
      CreateClubWizardSteps.NFTConfiguration,
      CreateClubWizardSteps.Roles,
      CreateClubWizardSteps.Members,
      CreateClubWizardSteps.Verification,
      CreateClubWizardSteps.FundraisingConfiguration,
      CreateClubWizardSteps.FinancialConfiguration,
      CreateClubWizardSteps.Preview,
    ],
  ],
  [
    GovernanceConfigurationType.ProjectFundraise,
    [
      CreateClubWizardSteps.BasicInfo,
      CreateClubWizardSteps.GovernanceConfiguration,
      CreateClubWizardSteps.Roles,
      CreateClubWizardSteps.Members,
      CreateClubWizardSteps.Verification,
      CreateClubWizardSteps.FundraisingConfiguration,
      CreateClubWizardSteps.FinancialConfiguration,
      CreateClubWizardSteps.Preview,
    ],
  ],
]);

export const governanceConfigurations: Array<IConfigurationItem> = [
  {
    name: "existing-token",
    title: "Deposit-based",
    description:
      "Club ownership and voting power are obtained based on the amount deposited during the fundraise.",
  },
  {
    name: "role-based",
    title: "Role-based",
    description:
      "The Founder (you) will assign voting power to specific roles. Each member will be given a role prior to club joining. Club ownership is obtained based on the amount deposited during the fundraise.",
  },
  {
    name: "nft",
    title: "NFT-based",
    description:
      "Voting power is based on the number of NFTs members own from the chosen collection. 1 NFT = 1 vote. Club ownership is obtained based on the amount deposited during the fundraise.",
  },
];

export const getProposalConfigurations = (
  treasury: IClubTreasury | undefined,
  clubType: ClubType,
  userRole: string,
  roleConfig: RoleConfig[]
): Array<IConfigurationItem> => {
  const configurationItems: IConfigurationItem[] = [
    {
      name: ProposalTypeNew.Discussion,
      title: "Discussion",
      description: `Made for discussing any topic within your project. Ask the ${
        clubType === ClubType.ProjectFundraise ? "investors" : "members"
      } to express their opinion by either accepting or rejecting the proposal.`,
      disabled:
        ((clubType === ClubType.TokenProportional ||
          clubType === ClubType.ProjectFundraise) &&
          treasury?.treasuryFundraiseStatus ===
            TreasuryFundraiseStatus.NoActiveFundraiseNoPreviousFundraises) ||
        treasury?.treasuryFundraiseStatus ===
          TreasuryFundraiseStatus.ActiveFundraise ||
        !checkIfUserHasPermissionForAction(
          userRole,
          roleConfig,
          ClubAction.CreateDiscussionProposal
        ),
    },
  ];
  //TODO@milica-monke-merge: Monke does not have TreasuryVault (Trading) proposals for now, for main instance uncomment this
  // clubType !== ClubType.ProjectFundraise &&
  //   configurationItems.push({
  //     name: ProposalTypeNew.TreasuryVault,
  //     title: "Trading",
  //     description:
  //       "Buy or sell digital assets with your club members. Execute the trade once the proposal reaches the predefined quorum.",
  //     disabled:
  //       treasury?.treasuryFundraiseStatus ===
  //         TreasuryFundraiseStatus.ActiveFundraise ||
  //       treasury?.treasuryFundraiseStatus ===
  //         TreasuryFundraiseStatus.NoActiveFundraiseNoPreviousFundraises ||
  //       !checkIfUserHasPermissionForAction(
  //         userRole,
  //         roleConfig,
  //         ClubAction.CreateP2PProposal
  //       ) ||
  //       !checkIfUserHasPermissionForAction(
  //         userRole,
  //         roleConfig,
  //         ClubAction.CreateMeProposal
  //       ),
  //   });

  configurationItems.push({
    name: ProposalTypeNew.Transfer,
    title: "Transfer funds",
    description:
      "Send tokens from the Treasury to any address. Subject to the limits and thresholds defined in the Proposal Voting Configuration..",
    disabled:
      treasury?.treasuryFundraiseStatus ===
        TreasuryFundraiseStatus.ActiveFundraise ||
      treasury?.treasuryFundraiseStatus ===
        TreasuryFundraiseStatus.NoActiveFundraiseNoPreviousFundraises ||
      !checkIfUserHasPermissionForAction(
        userRole,
        roleConfig,
        ClubAction.CreateTransferProposal
      ),
  });
  configurationItems.push({
    name: ProposalTypeNew.Withdrawal,
    title: "Withdrawal",
    description: `${
      clubType === ClubType.ProjectFundraise
        ? `Withdraw funds from the Treasury and distribute the withdrawn amount to eligible Investors, proportional to their Financial rights (ownership).`
        : `Withdraw funds from the Treasury and distribute the withdrawn amount to eligible
    club members, proportional to their financial rights.`
    }`,
    disabled:
      !checkIfUserHasPermissionForAction(
        userRole,
        roleConfig,
        ClubAction.CreateWithdrawalProposal
      ) ||
      treasury?.treasuryFundraiseStatus ===
        TreasuryFundraiseStatus.ActiveFundraise ||
      treasury?.treasuryFundraiseStatus ===
        TreasuryFundraiseStatus.NoActiveFundraiseNoPreviousFundraises,
  });

  configurationItems.push({
    name: ProposalTypeNew.UpdateClubConfig,
    description:
      clubType === ClubType.RoleBased
        ? "Change Voting Configuration for different types of proposals or Role's voting power."
        : "Change Voting Configuration for different types of proposals.",
    title: "Configuration changes",
    disabled:
      treasury?.treasuryFundraiseStatus ===
        TreasuryFundraiseStatus.ActiveFundraise ||
      treasury?.treasuryFundraiseStatus ===
        TreasuryFundraiseStatus.NoActiveFundraiseNoPreviousFundraises ||
      !checkIfUserHasPermissionForAction(
        userRole,
        roleConfig,
        ClubAction.CreateP2PProposal
      ) ||
      !checkIfUserHasPermissionForAction(
        userRole,
        roleConfig,
        ClubAction.CreateMeProposal
      ),
  });
  return configurationItems;
};
